import dayjs from "dayjs";
import { flatMap, some, tail, values } from "lodash";
import { PrimeIcons } from "primereact/api";
import { Button } from "primereact/button";
import { InputSwitch } from "primereact/inputswitch";
import { MenuItem } from "primereact/menuitem";
import { TieredMenu } from "primereact/tieredmenu";
import { Tooltip } from "primereact/tooltip";
import { Fragment, createRef, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown, TimeInput } from "../../../../common/components";
import { Card } from "../../../../common/components/Card";
import { WEEK_DAYS } from "../../../../common/constants";
import { getTimeFormatForRequest } from "../../../../common/utils/date.utils";
import { INTERVALS, NOTIFICATION_TYPES } from "../../constants";
import { Notification } from "../../models";

interface NotificationsConfigCardProps {
    title: string;
    description: string;
    notifications: Notification[];
    onChange?: (notification: Notification) => void;
}

const NotificationsConfigCard = (props: NotificationsConfigCardProps) => {
    const { title, description, notifications, onChange = () => {} } = props;

    const { t } = useTranslation("common");

    const [isExpanded, setIsExpanded] = useState(false);

    const notificationRefs = useRef([]);

    useEffect(() => {
        if (notifications) {
            notificationRefs.current = notifications.map(
                (_, i) => notificationRefs.current[i] ?? createRef()
            );
            setIsExpanded(some(flatMap(notifications, "active")));
        }
    }, [notifications]);

    const getItems = (notification: Notification): MenuItem[] => [
        {
            label: t("emailNotifications.interval.IMMEDIATELY") || "",
            visible: notification.type !== NOTIFICATION_TYPES.DEVICE_OVERVIEW,
            command: () => {
                onChange({ ...notification, interval: INTERVALS.IMMEDIATELY });
            },
        },
        {
            label: t("emailNotifications.interval.DAILY") || "",
            visible: notification.type !== NOTIFICATION_TYPES.INTEGRATION_LNS,
            command: () => {
                onChange({ ...notification, interval: INTERVALS.DAILY });
            },
        },
        {
            label: t("emailNotifications.interval.WEEKLY") || "",
            visible: notification.type !== NOTIFICATION_TYPES.INTEGRATION_LNS,
            items: tail(values(WEEK_DAYS)).map((weekDay) => ({
                label: t(`emailNotifications.interval.${weekDay}`) || "",
                command: () => {
                    // @ts-ignore
                    onChange({ ...notification, interval: weekDay });
                },
            })),
        },
    ];

    const Title = () => (
        <div className="flex flex-row align-items gap-2 relative -left-2.5">
            <Button
                className="text-default"
                icon={
                    isExpanded
                        ? PrimeIcons.CHEVRON_DOWN
                        : PrimeIcons.CHEVRON_RIGHT
                }
                rounded
                text
                severity="secondary"
                onClick={() => setIsExpanded((prevValue) => !prevValue)}
            />
            <div className="leading-loose">{title}</div>
        </div>
    );

    return (
        <Card
            title={Title}
            subTitle={description}
            className="md:w-[47rem] max-w-3xl"
        >
            <div className="grid gap-x-2 gap-y-1 grid-cols-8 grid-flow-row auto-rows-max items-center">
                {isExpanded &&
                    notifications.map((notification, index) => {
                        return (
                            <Fragment key={index}>
                                <div className="col-span-2">
                                    <div className="flex flex-row gap-2 items-center">
                                        {t(
                                            `emailNotifications.type.${notification.type}`
                                        )}
                                        <i
                                            className={`pi pi-info-circle info-icon-${notification.type}`}
                                        />
                                        <Tooltip
                                            className="max-w-xs"
                                            target={`.info-icon-${notification.type}`}
                                        >
                                            {t(
                                                `emailNotifications.tooltip.${notification.type}`
                                            )}
                                        </Tooltip>
                                    </div>
                                </div>
                                <InputSwitch
                                    className="col-span-1"
                                    checked={!!notification.active}
                                    onChange={(e) =>
                                        onChange({
                                            ...notification,
                                            active: !!e.value,
                                            interval: undefined,
                                        })
                                    }
                                />
                                <Dropdown
                                    className="col-span-3 h-12"
                                    disabled={!notification.active}
                                    // The placeholder shows both the value and the actual placeholder
                                    placeholder={
                                        t(
                                            notification.interval
                                                ? `emailNotifications.interval.${notification.interval}`
                                                : "emailNotifications.selectSchedule"
                                        ) || ""
                                    }
                                    pt={{
                                        list: { className: "!p-0" },
                                        emptyMessage: {
                                            className: "!p-0",
                                        },
                                        panel: {
                                            className: "invisible",
                                        },
                                        // The input text color is set for the placeholder with a value
                                        input: {
                                            className: notification.interval
                                                ? "!text-default"
                                                : "",
                                        },
                                    }}
                                    onClick={(event) =>
                                        notificationRefs?.current?.[
                                            index //@ts-ignore
                                        ]?.current?.toggle(event)
                                    }
                                />
                                <TieredMenu
                                    ref={notificationRefs.current[index]}
                                    popup
                                    model={getItems(notification)}
                                />
                                <div className="col-span-2">
                                    {notification.interval &&
                                        notification.interval !==
                                            INTERVALS.IMMEDIATELY && (
                                            <TimeInput
                                                className="h-12"
                                                value={notification.time}
                                                onChange={(value) => {
                                                    // check format first, set initial value to avoid
                                                    // that user has to enter a number twice at the start
                                                    const isTimeFormat = dayjs(
                                                        value,
                                                        "HH:mm",
                                                        true
                                                    ).isValid();

                                                    onChange({
                                                        ...notification,
                                                        time: isTimeFormat
                                                            ? getTimeFormatForRequest(
                                                                  value || ""
                                                              )
                                                            : value,
                                                    });
                                                }}
                                            />
                                        )}
                                </div>
                            </Fragment>
                        );
                    })}
            </div>
        </Card>
    );
};

export default NotificationsConfigCard;
